import { useCallback, useEffect, useState } from 'react'
import { api } from '../lib/api'
import type { Giftable, GiftablesReadiness, GiftablesResponse } from '../lib/types'

/**
 * Giftable games for a group (GET /groups/:id/giftables, §3.7) — every pick
 * the server derived where all but one member own the game, plus the roster
 * readiness that explains an empty list (any unknown library ⇒ `giftables` is
 * [] and `readiness` says why; the client never guesses ownership, ADR-019).
 * Fetches only while `enabled` (features.group_wallet).
 */
export function useGiftables(groupId: string | undefined, enabled: boolean) {
  const [giftables, setGiftables] = useState<Giftable[]>([])
  const [readiness, setReadiness] = useState<GiftablesReadiness | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(() => {
    if (!groupId || !enabled) return Promise.resolve()
    return api
      .get<GiftablesResponse>(`/groups/${groupId}/giftables`)
      .then((data) => {
        setGiftables(data.giftables)
        setReadiness(data.readiness)
        setError(null)
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Could not load giftable games.')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [groupId, enabled])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return {
    giftables,
    readiness,
    isLoading: enabled && !!groupId ? isLoading : false,
    error,
    refresh,
  }
}
